/** The two languages the UI ships in. Thai is the source of truth. */
export const SUPPORTED_LANGUAGES = ["th", "en"] as const;

export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = "th";

/** Matches the `vntr18.cookie-consent` convention in `lib/cookie-consent.ts`. */
export const LANGUAGE_STORAGE_KEY = "vntr18.lang";

export interface ResolveLanguageInput {
  /** `location.search`, e.g. "?lang=en". */
  search?: string;
  /** Previously persisted choice, or null. */
  stored?: string | null;
  /** `navigator.languages`, most-preferred first. */
  navigatorLanguages?: readonly string[];
}

/**
 * Pick the UI language: explicit `?lang=` wins, then a stored choice, then the
 * browser's preference order. Anything unrecognised is skipped, so a browser
 * asking for Japanese lands on Thai rather than on nothing.
 */
export function resolveLanguage(input: ResolveLanguageInput): Language {
  const fromQuery = matchLanguage(new URLSearchParams(input.search ?? "").get("lang"));
  if (fromQuery) return fromQuery;

  const fromStorage = matchLanguage(input.stored);
  if (fromStorage) return fromStorage;

  for (const tag of input.navigatorLanguages ?? []) {
    const fromNavigator = matchLanguage(tag);
    if (fromNavigator) return fromNavigator;
  }

  return DEFAULT_LANGUAGE;
}

/** Narrow a BCP-47 tag to a supported language by its base subtag. */
function matchLanguage(tag: string | null | undefined): Language | null {
  if (!tag) return null;
  const base = tag.toLowerCase().split("-")[0] ?? "";
  if (base === "en") return "en";
  if (base === "th") return "th";
  return null;
}
